/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './UnitSumCard.styles';
import { useIntl } from 'react-intl';
import { Button, Input, Modal, Tooltip, message } from 'antd';
import React, { Fragment, useRef, useState } from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { useDraggableCard } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderDnD/useDraggableCard';
import moment from 'moment';
import { useMutation, queryCache } from 'react-query';
import { submitVoucherResponse } from '~src/services/api/shares';
import {
  renderOptionalVoucherBadge,
  renderVoucherTypeIcon,
} from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/utils';
import { appLog } from '~src/utils/log';

export interface UnitSumCardProps {
  id: string;
  type: string;
  subType: number;
  cardTitle?: string;
  cardSubtitle?: string;
  cardQuantity?: number;
  unitType?: number;
  cardPrice?: number;
  cardSum?: number;
  borderless?: boolean;
  onContextMenu?: (e: React.MouseEvent) => void;
  onCardAction?: (action: string) => void;
  groupIndex?: number;
  onRightClick?: (e: React.MouseEvent) => void;
  setDraggingCardId?: (id: string | null) => void;
  isDragging?: boolean;
  isOnMyTender: boolean;
  tenderClosed?: boolean;
  isOfferLocked?: boolean;
  cardCreationDate: number;
  responseData?: {
    voucher_id: number;
    sum: number | null;
    is_approved: boolean | null;
  } | null;
  contractDataMyTender: {
    shareId: string;
    shareKey: string;
    contractOfferData: {
      is_offer_created: boolean;
      offer_id: number;
      state: number;
      contract_id: number;
    };
  };
  isDeleted?: boolean;
  contractDeleted?: boolean;
  isOptional?: boolean;
}

const UnitSumCard: React.FC<UnitSumCardProps> = ({
  id,
  subType,
  cardTitle,
  cardSubtitle,
  cardQuantity,
  unitType,
  cardPrice,
  cardSum,
  borderless,
  onContextMenu,
  onCardAction,
  groupIndex,
  onRightClick,
  setDraggingCardId,
  isDragging,
  isOnMyTender,
  tenderClosed,
  isOfferLocked,
  cardCreationDate,
  responseData,
  contractDataMyTender,
  isDeleted,
  contractDeleted,
  isOptional,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [sumValue, setSumValue] = useState<number | null>(
    responseData?.sum ?? null
  );

  const unitTypes = [
    { value: 1, label: intl.formatMessage({ id: 'centimeter' }) },
    { value: 2, label: intl.formatMessage({ id: 'meter' }) },
    { value: 3, label: intl.formatMessage({ id: 'square-meter' }) },
    { value: 4, label: intl.formatMessage({ id: 'cubic-meter' }) },
    { value: 5, label: intl.formatMessage({ id: 'piece' }) },
    { value: 6, label: intl.formatMessage({ id: 'sum' }) },
    { value: 7, label: intl.formatMessage({ id: 'kilogram' }) },
    { value: 8, label: intl.formatMessage({ id: 'ton' }) },
    { value: 9, label: intl.formatMessage({ id: 'timer' }) },
    { value: 10, label: intl.formatMessage({ id: 'day' }) },
    { value: 11, label: intl.formatMessage({ id: 'week' }) },
    { value: 12, label: intl.formatMessage({ id: 'month' }) },
  ];

  const [doSubmitResponse, { isLoading: isSubmitting }] = useMutation(
    submitVoucherResponse,
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'response-saved' }));
        queryCache.invalidateQueries([
          'voucher-responses',
          contractDataMyTender.shareId,
          contractDataMyTender.shareKey,
          contractDataMyTender.contractOfferData.offer_id,
        ]);
        queryCache.invalidateQueries([
          'contracts',
          undefined,
          undefined,
          contractDataMyTender.shareId,
          contractDataMyTender.shareKey,
        ]);
        setShowResponseModal(false);
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'response-save-failed' }));
      },
    }
  );

  const handleSubmitVoucherResponse = async () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;

    appLog('submitVoucherResponse pressed', { sumValue });

    if (
      !contractDataMyTender.contractOfferData.offer_id ||
      !contractDataMyTender.shareId ||
      !contractDataMyTender.shareKey
    ) {
      message.error(intl.formatMessage({ id: 'error-missing-offer-data' }));
      return;
    }

    if (sumValue === null) {
      message.error('Please enter a numeric response before submitting.');
      return;
    }

    await doSubmitResponse({
      share_id: contractDataMyTender.shareId,
      share_key: contractDataMyTender.shareKey,
      offer_id: contractDataMyTender.contractOfferData.offer_id,
      voucher_id: Number(id),
      data: { sum: sumValue, is_approved: null },
    });
  };

  const handleContextMenuClick = (item: DataContextMenuItemType) => {
    if (tenderClosed || isOfferLocked || contractDeleted) return;

    switch (item.modal) {
      case 'delete':
        onCardAction?.('delete');
        break;
      case 'reactivate-card':
        onCardAction?.('reactivate-card');
        break;
      case 'edit-card':
        onCardAction?.('edit-card');
        break;
    }
  };

  const filteredMenuItems = (isDeleted
    ? dataContextMenu.deletedContractCard
    : dataContextMenu.contractCard
  )
    .filter((item) => item.modal !== 'add-file')
    .map((item) => ({
      ...item,
      disabled:
        !isOnMyTender && (tenderClosed || isOfferLocked || contractDeleted)
          ? true
          : item.disabled,
    }));

  const cardRef = useRef<HTMLDivElement>(null);
  useDraggableCard(cardRef, id, {
    disabled:
      isOnMyTender ||
      tenderClosed ||
      isOfferLocked ||
      !!isDeleted ||
      !!contractDeleted,
    onDragStart: () => {
      if (!isOnMyTender) setDraggingCardId?.(id);
    },
    onDrop: () => {
      if (!isOnMyTender) setDraggingCardId?.(null);
    },
  });

  const dragDropTooltipTitle =
    !isOnMyTender && intl.formatMessage({ id: 'drag-and-drop-to-reorder' });

  const chosenUnitType = unitTypes.find((unit) => unit.value === unitType)
    ?.label;

  const RightClickWrapper = !isOnMyTender ? BaseRightClickOverlay : Fragment;
  const wrapperProps = !isOnMyTender
    ? {
        items: filteredMenuItems,
        onItemClick: handleContextMenuClick,
        onOverlayClose: () => {
          //
        },
      }
    : {};

  const canVendorRespond =
    isOnMyTender && contractDataMyTender.contractOfferData.is_offer_created;

  return (
    <RightClickWrapper {...wrapperProps}>
      <div
        ref={cardRef}
        css={[
          styles.cardWrapper,
          borderless && { border: 'none' },
          isDragging && styles.cardGhost,
          isDeleted && {
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            opacity: 0.88,
          },
        ]}
        onContextMenu={onContextMenu}>
        {isOnMyTender ? (
          /* VENDOR LAYOUT */
          <Fragment>
            {/* 1. Title & Description */}
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                flex: 4,
                alignSelf: 'center',
                overflow: 'hidden',
              }}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}>
                {isOptional && renderOptionalVoucherBadge(intl)}

                <div css={[styles.cardTitle, { fontSize: '16px' }]}>
                  {cardTitle}
                </div>
              </div>
              {cardSubtitle && (
                <div
                  css={{
                    marginTop: 4,
                    color: 'gray',
                    fontSize: '14px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                  {cardSubtitle}
                </div>
              )}
            </div>

            {/* 2. Count & Type */}
            <div
              css={{
                flex: 1, // Reduced to flex 1 to not take too much space
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center', // Center vertically
                fontSize: '14px',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                gap: '10px',
              }}>
              {(cardQuantity !== undefined || chosenUnitType) && (
                <Fragment>
                  {cardQuantity !== undefined && (
                    <span style={{ fontWeight: 'lighter' }}>
                      {intl.formatMessage({ id: 'count-label' })}{' '}
                      <span style={{ fontWeight: 'bold' }}>{cardQuantity}</span>
                    </span>
                  )}
                  {chosenUnitType && (
                    <span style={{ fontWeight: 'lighter' }}>
                      {intl.formatMessage({ id: 'type-label' })}{' '}
                      <span style={{ fontWeight: 'bold' }}>
                        {chosenUnitType}
                      </span>
                    </span>
                  )}
                </Fragment>
              )}
            </div>

            {/* 3. Response Value */}
            <div
              css={{
                flex: 1, // Adjusted to flex 1
                color: '#555',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end', // Align text right
                paddingRight: '15px', // Padding to separate from button
                whiteSpace: 'nowrap',
              }}>
              {canVendorRespond ? (
                responseData && responseData.sum !== null ? (
                  <span>
                    {intl.formatMessage({ id: 'response-label' })}{' '}
                    <b>{responseData.sum}</b>
                  </span>
                ) : (
                  <span style={{ color: '#999' }}>
                    {intl.formatMessage({ id: 'no-response-yet' })}
                  </span>
                )
              ) : null}
            </div>

            {/* 4. Response Button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              {canVendorRespond && (
                <Button
                  type="primary"
                  disabled={
                    !!tenderClosed ||
                    !!isOfferLocked ||
                    !!isDeleted ||
                    !!contractDeleted
                  }
                  onClick={() => {
                    if (
                      tenderClosed ||
                      isOfferLocked ||
                      isDeleted ||
                      contractDeleted
                    )
                      return;
                    setShowResponseModal(true);
                  }}>
                  {responseData && responseData.sum !== null
                    ? intl.formatMessage({ id: 'edit-btn' })
                    : intl.formatMessage({ id: 'set-btn' })}
                </Button>
              )}
            </div>

            {/* 5. Date & Type */}
            <div
              css={{
                textAlign: 'right',
                color: 'gray',
                fontSize: '12px',
              }}>
              <div>
                {/* {moment.unix(cardCreationDate).format('DD/MM/YYYY - HH:mm')} TV1-513 */}
              </div>
              {/* Replacement icon for vendor info */}
              <div style={{ marginTop: '4px' }}>
                {renderVoucherTypeIcon(subType, intl)}
              </div>
            </div>
          </Fragment>
        ) : (
          /* PROJECT LAYOUT (Original) */
          <Fragment>
            {/* Header */}
            <div css={styles.cardTitleWrapper}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isOptional && renderOptionalVoucherBadge(intl)}

                <div css={{ display: 'table', width: 'auto' }}>
                  <Tooltip
                    title={dragDropTooltipTitle}
                    placement="top"
                    mouseEnterDelay={0.5}
                    trigger={['hover']}>
                    <div
                      css={[
                        styles.cardTitle,
                        !cardSubtitle && { fontSize: '20px' },
                        { display: 'inline-block', width: 'auto' },
                      ]}>
                      {cardTitle}
                    </div>
                  </Tooltip>
                </div>
                {/* Voucher type icon for project */}
                {renderVoucherTypeIcon(subType, intl)}
              </div>
              <div
                css={styles.cardSubTitle}
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                {cardSubtitle}
              </div>
            </div>

            {/* Content: quantity + unit type */}
            <div css={styles.cardQuantity}>
              {(cardQuantity !== undefined || chosenUnitType) && (
                <Fragment>
                  {cardQuantity !== undefined && (
                    <span
                      style={{
                        fontWeight: 'lighter',
                      }}>
                      {intl.formatMessage({ id: 'count-label' })}{' '}
                      <span style={{ fontWeight: 'bold' }}>{cardQuantity}</span>
                    </span>
                  )}
                  {chosenUnitType && (
                    <span
                      style={{
                        fontWeight: 'lighter',
                        marginLeft: cardQuantity !== undefined ? 12 : 0,
                      }}>
                      {intl.formatMessage({ id: 'type-label' })}{' '}
                      <span style={{ fontWeight: 'bold' }}>
                        {chosenUnitType}
                      </span>
                    </span>
                  )}
                </Fragment>
              )}
            </div>

            {/* Kebab */}
            <div css={styles.cardOptionWrapper}>
              <IconButton
                icon={['far', 'ellipsis-v']}
                widthTooltip={intl.formatMessage({ id: 'settings' })}
                customStyle={{
                  cursor:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 0.5
                      : 1,
                }}
                click={(e) => {
                  onRightClick?.(e);
                  const fakeEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(fakeEvent);
                }}
              />
            </div>
          </Fragment>
        )}
      </div>

      {/* Vendor response modal – numeric only */}
      <Modal
        title={intl.formatMessage({ id: 'set-response-modal-title' })}
        visible={showResponseModal}
        confirmLoading={isSubmitting}
        okButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        cancelButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        onOk={handleSubmitVoucherResponse}
        onCancel={() => setShowResponseModal(false)}>
        <p style={{ marginBottom: 12 }}>
          {intl.formatMessage({ id: 'unit-sum-modal-desc' })}
        </p>
        <div style={{ marginBottom: 10 }}>
          <label>{intl.formatMessage({ id: 'sum-input-label' })}</label>
          <Input
            style={{ width: '100%' }}
            value={sumValue ?? ''}
            disabled={
              !!tenderClosed ||
              !!isOfferLocked ||
              !!isDeleted ||
              !!contractDeleted
            }
            onChange={(e) => {
              const value = e.target.value;
              setSumValue(value ? parseInt(value, 10) : null);
            }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
      </Modal>
    </RightClickWrapper>
  );
};

export default UnitSumCard;
